import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { FileDown, Loader2, Subtitles } from "lucide-react";
import { api } from "@/lib/api";

export default function SrtModal({ open, onOpenChange, projectId, projectName }) {
  const [maxChars, setMaxChars] = useState(42);
  const [maxLines, setMaxLines] = useState(2);
  const [srt, setSrt] = useState("");
  const [cueCount, setCueCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const { srt, cue_count } = await api.previewSrt(projectId, { max_chars_per_line: maxChars, max_lines_per_cue: maxLines });
      setSrt(srt); setCueCount(cue_count);
    } catch { toast.error("Could not build the SRT preview."); }
    setLoading(false);
  }, [open, projectId, maxChars, maxLines]);

  useEffect(() => { const t = setTimeout(refresh, 250); return () => clearTimeout(t); }, [refresh]);

  const download = async () => {
    setBusy(true);
    try {
      const blob = await api.downloadSrt(projectId, { max_chars_per_line: maxChars, max_lines_per_cue: maxLines });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${(projectName || "captions").replace(/[^a-z0-9_-]+/gi, "_")}.srt`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("SRT downloaded");
      onOpenChange(false);
    } catch { toast.error("Could not download the SRT."); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl border-white/10 bg-[#0d0d0d] text-white" data-testid="srt-modal">
        <DialogTitle className="flex items-center gap-2 font-display text-xl font-black"><Subtitles size={20} className="text-[#00F0FF]" /> Export SRT</DialogTitle>
        <DialogDescription className="sr-only">Configure line length and cue segmentation, preview the SRT, then download.</DialogDescription>

        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <div className="mb-2 flex items-center justify-between text-sm">
              <span className="font-semibold text-[#A1A1AA]">Max characters / line</span>
              <span className="font-bold tabular-nums text-white" data-testid="srt-mcpl-val">{maxChars}</span>
            </div>
            <Slider data-testid="srt-mcpl" min={10} max={60} step={1} value={[maxChars]} onValueChange={(v) => setMaxChars(v[0])} />
          </div>
          <div>
            <div className="mb-2 flex items-center justify-between text-sm">
              <span className="font-semibold text-[#A1A1AA]">Max lines / cue</span>
              <span className="font-bold tabular-nums text-white" data-testid="srt-mlpc-val">{maxLines}</span>
            </div>
            <Slider data-testid="srt-mlpc" min={1} max={4} step={1} value={[maxLines]} onValueChange={(v) => setMaxLines(v[0])} />
          </div>
        </div>

        <div className="mt-1">
          <div className="mb-1.5 flex items-center justify-between text-xs uppercase tracking-widest text-[#A1A1AA]">
            <span>Preview</span>
            <span className="tabular-nums text-[#666]" data-testid="srt-cue-count">{cueCount} cues</span>
          </div>
          <div className="relative">
            {loading && <div className="absolute right-3 top-3"><Loader2 size={15} className="animate-spin text-[#00F0FF]" /></div>}
            <textarea data-testid="srt-preview" readOnly value={srt}
              className="h-64 w-full resize-none rounded-xl border border-white/10 bg-black/60 p-3 font-plex text-[13px] leading-relaxed text-white/85 outline-none" />
          </div>
          <p className="mt-2 text-[11px] text-[#666]">Formatting only reflows text into readable cues — the underlying audio timing and word-level timestamps are never changed.</p>
        </div>

        <button onClick={download} disabled={busy || loading} data-testid="srt-download-confirm"
          className="mt-1 flex w-full items-center justify-center gap-2 rounded-full bg-white py-3 text-sm font-bold text-black transition-colors hover:bg-white/90 disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />} Download .srt
        </button>
      </DialogContent>
    </Dialog>
  );
}
